import {
  DependencyType,
  DependentResourceDc,
  DetailedTableInfoDc,
  unique,
  UpdateTableDc,
  GetTableListParams,
} from "@evergis/api";

import { createMutation, createResource } from "@evergis/react-fetching";
import { api as evergisApi } from "../evergis/api";
import { promiseAllSettled } from "../utils/promiseAllSettled";

import { ResourcesFetchParams } from "../modules/Catalog/hooks/useFetchParams";

export const tables = createResource(
  ({ category, ...params }: ResourcesFetchParams) =>
    async api => {
      return {
        data: await api.tables.findAll(params as unknown as GetTableListParams),
      };
    },
  { injected: evergisApi },
);

export const tableInfos = createResource(
  (names: string[]) => async api => ({
    data: await api.tables.getTableInfos(names),
  }),
  { injected: evergisApi },
);

export const table = createResource(
  (name: string) => async api => ({
    data: await api.tables.getTableInfo(name),
  }),
  { injected: evergisApi },
);

export const isLayerRef = (ref: DependentResourceDc) => ref.type === DependencyType.Layer;
export const isProjectRef = (ref: DependentResourceDc) => ref.type === DependencyType.Project;

export const tableWithRefs = createResource(
  (name: string) => async api => {
    const tableInfo = await api.tables.getTableInfo(name);
    const layerRefs = await getTableRefs(name, isLayerRef);

    return {
      data: {
        table: tableInfo,
        layers: await promiseAllSettled(layerRefs.map(layerName => api.layers.getLayerInfo(layerName))),
      },
    };
  },
  { injected: evergisApi },
);

export async function getTablesRefs(tableNames: string[], filter: (ref: DependentResourceDc) => boolean) {
  const results = await promiseAllSettled(tableNames.map(name => getTableRefs(name, filter)));

  return results.flat().filter(unique);
}

export async function getTableRefs(tableName: string, filter: (ref: DependentResourceDc) => boolean) {
  const refs = await evergisApi.tables.getResourceReferences(tableName);

  return refs.dependencies.filter(filter).map(({ name }) => name);
}

export const copyTables = createMutation(
  (tablesData: DetailedTableInfoDc[]) => async api => {
    const tablesPromises = tablesData.map(tableData => api.tables.create(tableData));

    return {
      data: {
        results: await promiseAllSettled([...tablesPromises]),
        invalidateProjects: tablesData.map(tableData => tableData.name),
      },
    };
  },
  {
    injected: evergisApi,
    update: () => {
      tables.invalidateAll();
    },
  },
);

export const updateTable = createMutation(
  (res: UpdateTableDc) => async api => ({
    data: await api.tables.update(res),
  }),
  {
    injected: evergisApi,
    update: updatedTable => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { preview, acl, ...tableToWrite } = updatedTable;

      table.write({
        input: updatedTable.name,
        reducer: value => ({ ...value, ...tableToWrite }),
      });

      tableWithRefs.write({
        input: updatedTable.name,
        reducer: value => ({ ...value, table: { ...value.table, ...tableToWrite } }),
      });
    },
  },
);

export const putTable = createMutation(
  (data: DetailedTableInfoDc) => async api => ({
    data: await api.tables.putTable(data.name, data),
  }),
  {
    injected: evergisApi,
    update: updatedTable => {
      const { preview, acl, ...tableToWrite } = updatedTable;

      table.write({
        input: updatedTable.name,
        reducer: value => ({ ...value, ...tableToWrite }),
      });

      tableWithRefs.write({
        input: updatedTable.name,
        reducer: value => ({ ...value, table: { ...value.table, ...tableToWrite } }),
      });
    },
  },
);

export const tableRemove = createMutation(
  (res: string) => async api => {
    await api.tables.remove(res);

    return { data: void 0 };
  },
  {
    injected: evergisApi,
  },
);
